/**
 * Creates a validation warning object with optional location metadata
 * 
 * @param {string} text - The warning message to display
 * @param {Object} [location] - Optional location information for navigation
 * @param {number} [location.tab] - Tab index to switch to (0-based)
 * @param {number|number[]} [location.panel] - Panel index(es) to expand (0-based, can be single or array)
 * @param {string} [location.target] - `data-validation-target` value to find the element
 * @returns {Object} Standardized validation warning object
 * 
 * @example
 * // Warning with full navigation path
 * // In template: <v-text-field data-validation-target="formNameInput" ... />
 * mixinCreateWarning('A Form Name is required.', {
 *   tab: 0,
 *   panel: 0,
 *   target: 'formNameInput'
 * })
 * 
 * @example
 * // Warning with multiple panels to expand
 * // In template: <div data-validation-target="logoImageInput">...</div>
 * mixinCreateWarning('Logo is required.', {
 *   tab: 1,
 *   panel: [1, 2],
 *   target: 'logoImageInput'
 * })
 * 
 * @example
 * // Warning with just panel and target (no tab switching)
 * mixinCreateWarning('Invalid endpoint.', {
 *   panel: 1,
 *   target: 'sendFromEndpointSelect'
 * })
 * 
 * @example
 * // Warning with just target (direct scroll, no navigation)
 * mixinCreateWarning('Age must be valid.', {
 *   target: 'ageGateInput'
 * })
 * 
 * @example
 * // Warning with no location (just message display)
 * mixinCreateWarning('General configuration error.')
 */
function mixinCreateWarning(text, location = null) {
  const warning = { text };
  
  if (location) {
    warning.location = {};
    
    if (typeof location.tab === 'number') {
      warning.location.tab = location.tab;
    }
    
    if (location.panel !== undefined) {
      warning.location.panel = location.panel;
    }
    
    if (location.target) {
      warning.location.target = location.target;
    }
  }
  
  return warning;
}

export default {
  methods: {
    /**
     * Factory method for creating validation warnings.
     * Available as this.mixinCreateWarning() in any component using this mixin.
     */
    mixinCreateWarning,

    /**
     * Navigates to and highlights a form element based on validation warning location.
     * Handles optional tab switching, panel expansion, and element scrolling/highlighting.
     * Uses native DOM methods to find and scroll to elements with `data-validation-target` attributes, avoiding complications with nested Vue refs.
     *
     * @param {Object} warning - Validation warning object
     * @param {Object} warning.location - Location information
     * @param {number} [warning.location.tab] - Tab index to switch to (optional)
     * @param {number|number[]} [warning.location.panel] - Panel index(es) to expand (optional)
     * @param {string} [warning.location.target] - `data-validation-target` value of element to highlight (optional)
     * @param {Object} options - Configuration options
     * @param {string} [options.active_tab_model] - Stringified name of the Vue data property controlling active tab
     * @param {string} [options.panel_model] - Stringified name of the Vue data property controlling expanded panels
     * @param {number} [options.scroll_delay=200] - Delay before scrolling (ms)
     * @param {number} [options.highlight_duration=2500] - How long to show highlight (ms)
     * @param {string} [options.highlight_class='highlight-error'] - CSS class for highlighting
     */
    mixinGuideToFix(warning, options = {}) {
      if (!warning?.location) return;

      const {
        active_tab_model = null,
        panel_model = null,
        scroll_delay = 200,
        highlight_duration = 2500,
        highlight_class = 'highlight-error',
      } = options;

      const { tab, panel, target } = warning.location;

      if (typeof tab === 'number' && active_tab_model && this[active_tab_model] !== tab) {
        this[active_tab_model] = tab;
      }

      this.$nextTick(() => {
        if (panel !== undefined && panel_model) {
          this._expandPanel(panel, panel_model);
        }

        this.$nextTick(() => {
          if (target) {
            this.scrollToAndHighlightElement(target, {
              scroll_delay,
              highlight_duration,
              highlight_class,
            });
          }
        });
      });
    },

    /**
     * Expands one or more panels in an expansion panel group
     * @param {number|number[]} panel - Panel index or array of indices to expand
     * @param {string} panel_model - Name of the data property controlling panels
     */
    _expandPanel(panel, panel_model) {
      if (!this[panel_model]) return;

      const panels_to_expand = Array.isArray(panel) ? panel : [panel];

      panels_to_expand.forEach(panel_index => {
        if (!this[panel_model].includes(panel_index)) {
          this[panel_model].push(panel_index);
        }
      });
    },

    /**
     * Scrolls to and highlights a form element using data-validation-target attribute.
     * Also attempts to focus the first focusable input within the element.
     * @param {string} target - data-validation-target value
     * @param {Object} options - Scroll and highlight options
     */
    scrollToAndHighlightElement(target, options = {}) {
      const {
        scroll_delay = 200,
        highlight_duration = 2500,
        highlight_class = 'highlight-error',
      } = options;

      setTimeout(() => {
        const element = document.querySelector(`[data-validation-target="${target}"]`);
        
        if (!element) {
          //console.warn(`Element with data-validation-target="${target}" not found`);
          return;
        }

        if (typeof element.scrollIntoView === 'function') {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add(highlight_class);

          this._setAriaInvalid(element, true);

          setTimeout(() => {
            element.classList.remove(highlight_class);
            this._setAriaInvalid(element, false);
          }, highlight_duration);

          this._announceToScreenReader(`Navigated to validation error: ${target}`);

          this._focusFirstInput(element);
        } else {
          //console.warn(`Element with data-validation-target="${target}" does not support scrollIntoView`);
        }
      }, scroll_delay);
    },

    /**
     * Sets or removes aria-invalid attribute on an element and its inputs
     * @param {HTMLElement} element - The element to mark
     * @param {boolean} invalid - Whether the element is invalid
     */
    _setAriaInvalid(element, invalid) {
      try {
        const inputs = element.querySelectorAll('input, textarea, select');
        
        if (inputs.length > 0) {
          inputs.forEach(input => {
            if (invalid) {
              input.setAttribute('aria-invalid', 'true');
            } else {
              input.removeAttribute('aria-invalid');
            }
          });
        } else if (element.matches('input, textarea, select')) {
          if (invalid) {
            element.setAttribute('aria-invalid', 'true');
          } else {
            element.removeAttribute('aria-invalid');
          }
        }
      } catch (error) {
        // no-op
      }
    },

    /**
     * Announces a message to screen readers using aria-live region
     * @param {string} message - Message to announce
     */
    _announceToScreenReader(message) {
      try {
        let liveRegion = document.getElementById('validation-live-region');
        
        if (!liveRegion) {
          liveRegion = document.createElement('div');
          liveRegion.id = 'validation-live-region';
          liveRegion.setAttribute('role', 'status');
          liveRegion.setAttribute('aria-live', 'polite');
          liveRegion.setAttribute('aria-atomic', 'true');
          liveRegion.className = 'sr-only';
          liveRegion.style.position = 'absolute';
          liveRegion.style.left = '-10000px';
          liveRegion.style.width = '1px';
          liveRegion.style.height = '1px';
          liveRegion.style.overflow = 'hidden';
          document.body.appendChild(liveRegion);
        }

        liveRegion.textContent = '';
        setTimeout(() => {
          liveRegion.textContent = message;
        }, 100);
      } catch (error) {
        // no-op
      }
    },

    /**
     * Finds and focuses the first focusable input element within a container.
     * Silently fails if no focusable element is found.
     * @param {HTMLElement} container - Container element to search within
     */
    _focusFirstInput(container) {
      try {
        const focusable_selectors = [
          'input:not([disabled]):not([type="hidden"])',
          'textarea:not([disabled])',
          'select:not([disabled])',
          'button:not([disabled])',
          '[contenteditable="true"]',
          '[tabindex]:not([tabindex="-1"])',
        ].join(', ');
        let focusable_element = null;
        if (container.matches(focusable_selectors)) {
          focusable_element = container;
        } else {
            focusable_element = container.querySelector(focusable_selectors);
        }

        if (focusable_element && typeof focusable_element.focus === 'function') {
          setTimeout(() => {
            const originalTabIndex = focusable_element.getAttribute('tabindex');
            
            if (originalTabIndex === null && !focusable_element.matches('input, textarea, select, button, a')) {
              focusable_element.setAttribute('tabindex', '-1');
            }

            focusable_element.focus();
            
            if (
              (focusable_element.tagName === 'INPUT' || focusable_element.tagName === 'TEXTAREA') &&
              focusable_element.select &&
              focusable_element.value
            ) {
              focusable_element.select();
            }
          }, 100);
        }
      } catch (error) {
        // no-op
      }
    },
  },
};